import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateChild } from '@angular/router';
import { HttpRequestService } from '../common-services/http-request.service';
import { ToastrService } from 'ngx-toastr';
import { PermissionsService } from '../common-services/permissions.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardGuard implements CanActivate, CanActivateChild {

  constructor(
    private authservice: HttpRequestService,
    private router: Router,
    private toastr: ToastrService,
    private permissionsService: PermissionsService
  ) { }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const url = state.url;
    if (url.includes('terms-and-service') || url.includes('privacy-policy')) {
      return true;
    }
    if (this.authservice.loggedIn()) {
      if (!this.checkRoutePermission(next)) {
        return false;
      }
      if (!this.checkReadOnlyRestriction(state.url)) {
        return false;
      }
      return this.checkGcRestriction(state.url);
    } else {
      this.router.navigateByUrl('/auth');
      return false;
    }
  }

  canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.canActivate(next, state);
  }

  private checkGcRestriction(url: string): boolean {
    const userDataStr = localStorage.getItem('user_data');
    if (!userDataStr) {
      return true;
    }

    try {
      const currentUser = JSON.parse(userDataStr);
      const roleId = currentUser?.role?.id ? Number(currentUser.role.id) : null;
      if (roleId === 1) {
        const urlWithoutQuery = url.split('?')[0].split('#')[0].toLowerCase();
        const pathSegments = urlWithoutQuery.split('/').filter(s => s.length > 0);
        const lastSegment = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : '';

        const isListRoute = lastSegment.endsWith('-list') || lastSegment === 'list';

        const editKeywords = ['new', 'edit', 'view', 'update', 'create'];
        const hasEditKeyword = pathSegments.some(seg => editKeywords.includes(seg)) ||
          editKeywords.some(kw => url.toLowerCase().includes('/' + kw) || url.toLowerCase().includes('=' + kw));

        const crudModules = [
          'master', 'users', 'requisition', 'supply', 'project',
          'sub-contractor', 'supplier', 'bulk-notification',
          'summary-dlr-tradewise', 'summary-dlr-vendorwise', 'sub-contractor-performance'
        ];

        const staticKeywords = ['filter', 'connect', 'dashboard', 'notification', 'gc-report', 'altius', 'youcon', 'gc', 'lc', 'sprint', 'bo', 'whatsapp', 'gcdump', 'lcdump', 'inapp', 'import'];
        const isStaticEndpoint = staticKeywords.includes(lastSegment);

        const isCrudModulePage = crudModules.some(m => urlWithoutQuery.includes('/' + m));
        const isCreateOrEditRoute = !isListRoute && !isStaticEndpoint && (hasEditKeyword || isCrudModulePage);

        const isExcludedRoute = urlWithoutQuery.includes('/general-contractor') || urlWithoutQuery.includes('/users/users');

        if (isCreateOrEditRoute && !isExcludedRoute) {
          const gcId = localStorage.getItem('header_gc_id');
          const gcIdNum = gcId ? parseInt(gcId, 10) : 0;
          if (gcIdNum === 0 || isNaN(gcIdNum)) {
            this.toastr.error("Please select a company from the header dropdown before creating or editing records.");

            // If the user lands directly on a creation/edit URL, redirect them to the dashboard
            if (this.router.url === '/' || this.router.url === '/auth') {
              this.router.navigateByUrl('/dashboard');
            }
            return false;
          }
        }
      }
    } catch (e) {
      console.error('Error parsing user data in AuthGuard', e);
    }

    return true;
  }

  private checkRoutePermission(route: ActivatedRouteSnapshot): boolean {
    let currentRoute: ActivatedRouteSnapshot | null = route;
    while (currentRoute) {
      if (currentRoute.data && currentRoute.data['permission']) {
        const requiredPermission = currentRoute.data['permission'];
        if (!this.permissionsService.hasPermission(requiredPermission)) {
          this.toastr.error('You do not have access to this page.');
          this.router.navigate(['/dashboard']);
          return false;
        }
      }
      currentRoute = currentRoute.parent;
    }
    return true;
  }

  private checkReadOnlyRestriction(url: string): boolean {
    const urlLower = url.split('?')[0].split('#')[0].toLowerCase();

    // 1. Check if global read-only is active
    const isGlobalReadOnly = this.permissionsService.isReadOnly();

    // Pure creation / import routes that have no existing record to view
    const creationKeywords = ['/new', '/create', '/import'];
    const isCreationRoute = creationKeywords.some(kw => urlLower.endsWith(kw) || urlLower.includes(kw + '/'));

    if (isGlobalReadOnly && isCreationRoute) {
      this.toastr.warning('Creating new records and importing are disabled in Read-Only mode.', 'Read-Only');
      this.router.navigate(['/dashboard']);
      return false;
    }

    // 2. Component-wise check: extract feature from URL path (e.g. /sub-contractor, /project, etc.)
    const segments = urlLower.split('/').filter(s => s.length > 0);
    if (segments.length > 0 && isCreationRoute) {
      const featureSegment = segments[0] === 'pre-contract' && segments.length > 1 ? segments[1] : segments[0];
      if (this.permissionsService.isComponentReadOnly(featureSegment)) {
        this.toastr.warning(`This module is in Read-Only mode. Creating new records is not permitted.`, 'Read-Only');
        this.router.navigate(['/dashboard']);
        return false;
      }
    }

    return true;
  }
}

